import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Disposition = {
    Open: "o",
    Closed: "c",
    Withdrawn: "w",
    Pending: "p",
    OnHold: "h"
};
export type DispositionType = typeof Disposition[keyof typeof Disposition];
export type MatterHourlyRates = {id: uuid, type: string, defaultRate: number, rate: number};
export type MatterContingency = {judgement: number, clientPaysExpenses: boolean, commissionPercentage: number, retainerFee: number};
class Matter extends Base implements EntityType {
    matterId: uuid;
    leadId?: uuid | null;
    caseTypeId?: uuid | null;
    name: string;
    clients: uuid[];
    assignedTo: uuid[];
    code: string;
    disposition: DispositionType;
    closeDate?: Date | null;
    materialsSentToClientDate?: Date | null;
    maxClientFunds?: number | null;
    startingBilled?: number | null;
    startingCollected?: number | null;
    rateType: string;
    hourlyRates: MatterHourlyRates[];
    contingency?: MatterContingency | null;
    billingIncrement: number;
    expenses: uuid[];
    minimumBalance?: number | null;
    caseNumber?: string | null;
    courtIds: uuid[];
    billToContactId?: uuid | null;
    restricted: boolean;
    constructor(doc: typeof Matter | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            matterId: genUUID(),
            name: null,
            clients: [],
            code: null,
            disposition: "o",
            rateType: null,
            restricted: false,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "matter";
        // @ts-ignore
        this.matterId = doc._matterId || doc.matterId || doc.matterid || genUUID();
        // @ts-ignore
        this.leadId = doc._leadId || doc.leadId || doc.leadid || null;
        // @ts-ignore
        this.caseTypeId = doc._caseTypeId || doc.caseTypeId || doc.casetypeid || null;
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.clients = doc._clients || doc.clients || [];
        // @ts-ignore
        this.assignedTo = doc._assignedTo || doc.assignedTo || doc.assignedto || [];
        // @ts-ignore
        this.code = doc._code || doc.code || null;
        // @ts-ignore
        this.disposition = util.trimRight(doc._disposition) || util.trimRight(doc.disposition) || "o";
        // @ts-ignore
        this.closeDate = DateUtil.dateOnly(doc._closeDate) || DateUtil.dateOnly(doc.closeDate) || DateUtil.dateOnly(doc.closedate) || null;
        // @ts-ignore
        this.materialsSentToClientDate = DateUtil.dateOnly(doc._materialsSentToClientDate) || DateUtil.dateOnly(doc.materialsSentToClientDate) || DateUtil.dateOnly(doc.materialssenttoclientdate) || null;
        // @ts-ignore
        this.maxClientFunds = util.coalesce(util.parseFloat(doc._maxClientFunds), util.parseFloat(doc.maxClientFunds), util.parseFloat(doc.maxclientfunds), null);
        // @ts-ignore
        this.startingBilled = util.coalesce(util.parseFloat(doc._startingBilled), util.parseFloat(doc.startingBilled), util.parseFloat(doc.startingbilled), null);
        // @ts-ignore
        this.startingCollected = util.coalesce(util.parseFloat(doc._startingCollected), util.parseFloat(doc.startingCollected), util.parseFloat(doc.startingcollected), null);
        // @ts-ignore
        this.rateType = doc._rateType || doc.rateType || doc.ratetype || null;
        // @ts-ignore
        this.hourlyRates = doc._hourlyRates || doc.hourlyRates || doc.hourlyrates || [];
        // @ts-ignore
        this.contingency = doc._contingency || doc.contingency || {};
        // @ts-ignore
        this.billingIncrement = util.coalesce(util.parseInt(doc._billingIncrement), util.parseInt(doc.billingIncrement), util.parseInt(doc.billingincrement), 1);
        // @ts-ignore
        this.expenses = doc._expenses || doc.expenses || [];
        // @ts-ignore
        this.minimumBalance = util.coalesce(util.parseFloat(doc._minimumBalance), util.parseFloat(doc.minimumBalance), util.parseFloat(doc.minimumbalance), null);
        // @ts-ignore
        this.caseNumber = doc._caseNumber || doc.caseNumber || doc.casenumber || null;
        // @ts-ignore
        this.courtIds = doc._courtIds || doc.courtIds || doc.courtids || [];
        // @ts-ignore
        this.billToContactId = doc._billToContactId || doc.billToContactId || doc.billtocontactid || null;
        // @ts-ignore
        this.restricted = util.defaultBoolean([doc.restricted, doc._restricted, doc.restricted, false]);
    }
    get id(): uuid { return this.matterId; }
    addClient(client: uuid): uuid {
        if (!client) throw new ValidationError('No nulls allowed in an array!');
        if (!this.clients) this.clients = [];
        this.clients.push(client);
        return client;
    }
    updateClient(client: uuid) {
        if (!this.clients) this.clients = [];
        const index = this.clients.findIndex(item => item === client);
        if (index !== -1) { this.clients[index] = client; }
        else this.addClient(client);
    }
    removeClient(client: uuid) {
        if (!this.clients) return;
        let foundItems = this.clients?.filter(item => item === client);
        if (foundItems && foundItems.length > 0) {
            this.clients = this.clients.filter(item => !(item === client));
        }
    }
    abandonClients() { this.clients = []; }
    addAssignedTo(assignedTo: uuid): uuid {
        if (!assignedTo) throw new ValidationError('No nulls allowed in an array!');
        if (!this.assignedTo) this.assignedTo = [];
        this.assignedTo.push(assignedTo);
        return assignedTo;
    }
    updateAssignedTo(assignedTo: uuid) {
        if (!this.assignedTo) this.assignedTo = [];
        const index = this.assignedTo.findIndex(item => item === assignedTo);
        if (index !== -1) { this.assignedTo[index] = assignedTo; }
        else this.addAssignedTo(assignedTo);
    }
    removeAssignedTo(assignedTo: uuid) {
        if (!this.assignedTo) return;
        let foundItems = this.assignedTo?.filter(item => item === assignedTo);
        if (foundItems && foundItems.length > 0) {
            this.assignedTo = this.assignedTo.filter(item => !(item === assignedTo));
        }
    }
    abandonAssignedTo() { this.assignedTo = []; }
    static DISPOSITION_VALUES = ["o", "c", "w", "p", "h"];
    addHourlyRate(hourlyRate: MatterHourlyRates): MatterHourlyRates {
        if (!hourlyRate) throw new ValidationError('No nulls allowed in an array!');
        if (!this.hourlyRates) this.hourlyRates = [];
        this.hourlyRates.push(hourlyRate);
        return hourlyRate;
    }
    updateHourlyRate(hourlyRate: MatterHourlyRates) {
        if (!this.hourlyRates) this.hourlyRates = [];
        const index = this.hourlyRates.findIndex(item => item === hourlyRate);
        if (index !== -1) { this.hourlyRates[index] = hourlyRate; }
        else this.addHourlyRate(hourlyRate);
    }
    removeHourlyRate(hourlyRate: MatterHourlyRates) {
        if (!this.hourlyRates) return;
        let foundItems = this.hourlyRates?.filter(item => item === hourlyRate);
        if (foundItems && foundItems.length > 0) {
            this.hourlyRates = this.hourlyRates.filter(item => !(item === hourlyRate));
        }
    }
    abandonHourlyRates() { this.hourlyRates = []; }
    addExpense(expense: uuid): uuid {
        if (!expense) throw new ValidationError('No nulls allowed in an array!');
        if (!this.expenses) this.expenses = [];
        this.expenses.push(expense);
        return expense;
    }
    updateExpense(expense: uuid) {
        if (!this.expenses) this.expenses = [];
        const index = this.expenses.findIndex(item => item === expense);
        if (index !== -1) { this.expenses[index] = expense; }
        else this.addExpense(expense);
    }
    removeExpense(expense: uuid) {
        if (!this.expenses) return;
        let foundItems = this.expenses?.filter(item => item === expense);
        if (foundItems && foundItems.length > 0) {
            this.expenses = this.expenses.filter(item => !(item === expense));
        }
    }
    abandonExpenses() { this.expenses = []; }
    addCourtId(courtId: uuid): uuid {
        if (!courtId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.courtIds) this.courtIds = [];
        this.courtIds.push(courtId);
        return courtId;
    }
    updateCourtId(courtId: uuid) {
        if (!this.courtIds) this.courtIds = [];
        const index = this.courtIds.findIndex(item => item === courtId);
        if (index !== -1) { this.courtIds[index] = courtId; }
        else this.addCourtId(courtId);
    }
    removeCourtId(courtId: uuid) {
        if (!this.courtIds) return;
        let foundItems = this.courtIds?.filter(item => item === courtId);
        if (foundItems && foundItems.length > 0) {
            this.courtIds = this.courtIds.filter(item => !(item === courtId));
        }
    }
    abandonCourtIds() { this.courtIds = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this matter"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.matterId; }
    static override getNonRefArrayFields(): string[] { return ["clients", "assignedTo", "hourlyRates", "expenses", "courtIds"]; }
    static override getDefaultEntries(): Matter[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'matter',
    dataArea: 'Cases',
    attachedTo: {parents: ['contact'], via: 'clients', onDelete: 'block', onReplace: 'move'},
    table: 'MATTER',
    comment: 'A matter (also called a case in the UI) represents a legal engagement for one or more clients. It tracks the case type, disposition, assigned team, billing structure (hourly, flat fee, or contingency), trust account minimums, and associated courts. Each matter has an autogenerated code and may originate from a lead.',
    fields: {
        matterId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this matter'
        },

        /**
         * Lead associated with this case.  Not required, as imported cases will likely not have leads.
         */
        leadId: {
            type: 'uuid',
            entityType: 'lead',
            comment: 'The lead that generated this matter, if any. Many firms skip leads and work directly from cases.'
        },

        /**
         * CaseType for this case
         */
        caseTypeId: {
            type: 'uuid',
            entityType: 'caseType',
            comment: 'The UUID of the type of case, e.g. Traffic Citation or Divorce'
        },

        /**
         * Reference name for the case.  This need not be unique (code should be unique)
         */
        name: {
            type: 'string',
            required: true,
            comment: 'Names are generally displayed based on a template, but they may also be manually named'
        },

        /**
         * Clients for this case
         */
        clients: {
            type: 'uuid[]',
            entityType: 'contact',
            required: true,
            comment: 'Clients represented in this case'
        },

        /**
         * Team assigned to this case.  Can be a mix of lawyers and paralegals.
         * In the future this might be used to limit who can see the case...
         */
        assignedTo: {
            type: 'uuid[]',
            entityType: 'user',
            comment: 'Who is this case assigned to?'
        },

        /**
         * This is an autogenerated code, e.g. 2019-DVC-0001 (first divorce case in 2019).
         * Uses CaseType.code.
         */
        code: {type: 'string', required: true, comment: 'Autogenerated code'},

        /**
         * Current disposition of the case. [o]pen, [c]losed, [w]ithdrawn, [p]ending, on [h]old.
         */
        disposition: {
            type: 'char',
            required: true,
            values: ['o', 'c', 'w', 'p', 'h'],
            valueNames: ['Open', 'Closed', 'Withdrawn', 'Pending', 'On Hold'],
            default: 'o',
            comment: 'Disposition of the Case ([o]pen/[c]losed/[w]ithdrawn/[p]ending/on [h]old)'
        },

        /**
         * Date the case was closed.
         */
        closeDate: {type: 'date', comment: 'Date the case was closed'},

        /**
         * Once a case is closed, we need to send them back all their stuff!
         */
        materialsSentToClientDate: {type: 'date', comment: 'Date client materials were returned'},

        /**
         * If a client has a drop-dead amount of funds, or the case is being paid for by a 3rd party like a legal
         * insurance company, set the limit here.
         */
        maxClientFunds: {
            type: 'money',
            comment: 'Maximum funds the client can provide, or maximum payout from a 3rd party'
        },

        /**
         * For an imported case, what is the amount that has already been billed?  This assumes no tasks were imported.
         */
        startingBilled: {type: 'money', comment: 'If an imported case, amount billed so far'},

        /**
         * For an imported case, what is the amount that has already been collected?  This assumes no tasks or invoices were imported.
         */
        startingCollected: {type: 'money', comment: 'If an imported case, amount collected so far'},

        /**
         * Rate types, including a mixed type (which means use whatever is any any of the three other fields).
         * If we have a mixed rate type, e.g. 'cfh', then we need to look into each of the hourlyRates, flatFee and contingency buckets.
         *
         * For example, if we have entries in hourlyRates and contingency, the rate type would be 'ch', and it means that we charge hourly rates,
         * AND we get a contingency fee at the end.  If there are hourly rates and flat fees, then we charge to flat fees when the
         * user tells us to, otherwise we charge hourly rates.
         *
         * Note that contingency is only calculated at the end of the case, once the judgement is known.  Until that time,
         * all entries are either billed at hourly rates (if hourly rates are available).
         */
        rateType: {
            type: 'string',
            required: true,
            comment: 'What is the rate structure for this matter?  [h]ourly, [f]lat Fee, [c]ontingency or a combination'
        },

        /**
         * An array of override rates for users in the system.  Must include the rate for every user,
         * as the default rates for a user may go up over time, but contracts usually specify a specific rate.
         *
         * We keep track of the id, type and rate, where type is:
         * type: char ([u]ser, [g]roup, [r]ole).
         *
         * Example:
         * [
         *   {id: userId, type: 'u', defaultRate: 250.00, rate: 200.00},  (e.g. user josie will bill at $200.00/hr, not her default of 250.00)
         *   {id: roleId, type: 'r', rate: 100.00},  (e.g. role of paralegal means all paralegals are $100.00/hr)
         *   {id: groupId, type: 'g', rate: 200.00}  (e.g. the group 'criminal' all bills at $200.00/hr)
         * ]
         */
        hourlyRates: {
            type: 'json[]',
            jsdoc: '{id: uuid, type: string, defaultRate: number, rate: number}',
            entity: {
                type: 'user',
                value: 'id',
                deleteStrategy: 'preserve' // Keep for historical purposes
            },
            comment: 'Hourly rates for each user, group or role. If not specified here, defaults apply'
        },

        /**
         * Since the amount of the judgement is not known up front, we don't really know what the amount or the commission
         * will be.  Thus this information will be filled in at the end of the case.  There are, however, a few settings
         * that need to be tracked, like:
         * - Will the client be paying expenses?  This is usually true.
         * - Is the client paying a retainer fee?  This is uncommon, but some lawyers use it as a way to ensure the client is serious.
         *
         * Example of stepped contingency fees:
         * {
         *   judgement: 25000,
         *   commissionPercentage: 33,
         *   clientPaysExpenses: true,
         *   retainerFee: number
         * }
         */
        contingency: {
            type: 'json',
            jsdoc: '{judgement: number, clientPaysExpenses: boolean, commissionPercentage: number, retainerFee: number}',
            comment: 'Amount of the judgement, and the percentage of commission from the proceeds of the case'
        },

        /**
         * Overrides the billingIncrement from the firm level.  Only applies to hourly rates.
         */
        billingIncrement: {
            type: 'int', default: 1, comment: 'Minimum number of minutes for the billing increment'
        },

        /** Expenses for the case */
        expenses: {
            type: 'uuid[]',
            entityType: 'expense',
            comment: 'Expenses related to this matter'
        },

        /**
         * Minimum amount required in the trust account for this matter
         */
        minimumBalance: {
            type: 'money', comment: 'Minimum trust balance'
        },

        /*
         * Case/Cause/Docket Number - a unique number assigned by the court for a case
         */
        caseNumber: {
            type: 'string',
            comment: 'Case/Cause/Docket Number for a case'
        },

        /* Courts associated with this case */
        courtIds: {
            type: 'uuid[]',
            entityType: 'court',
            comment: 'Courts associated with this case'
        },

        /**
         * Optional bill-to contact for this case. When set, invoices for this matter are
         * addressed to this contact (its billing name + delivery address) instead of the
         * client(s) — e.g. billing a third-party payer such as Texas Legal. See OWA-320.
         */
        billToContactId: {
            type: 'uuid',
            entityType: 'contact',
            comment: 'Optional bill-to contact; invoices for this case are addressed here instead of the client'
        },
        restricted: {
            type: 'boolean',
            serverOwned: true,
            required: true,
            default: false,
            comment: 'A Restricted Case is visible only to its team, owners and admins, whatever the firm setting. An empty team leaves only owners and admins. Set only by owners and admins through server routes.'
        }
    },
    indexes: [
        {name: 'hasLead', fn: m => !!m.leadId},
        {name: 'open', fn: m => m.disposition === 'o'},
        {name: 'fatalError', fn: t => t.fatalError && (t.fatalError.message || t.fatalError.status)},
    ],
    maps: [
        {
            name: 'leadId',
            keysFn: m => m.leadId,
            presentFn: (m, k) => m.leadId === k && m.status !== 'D'
        },
        {name: 'caseTypeId', keysFn: l => l.caseTypeId, presentFn: (t, k) => t.caseTypeId === k},
        {name: 'contactId', keysFn: t => t.clients, presentFn: (t, k) => t.clients?.includes(k)},
        {name: 'userId', keysFn: t => t.assignedTo, presentFn: (t, k) => t.assignedTo?.includes(k)},
        {name: 'courtId', keysFn: t => t.courtIds, presentFn: (t, k) => t.courtIds?.includes(k)},
        {name: 'billToContactId', keysFn: t => t.billToContactId, presentFn: (t, k) => t.billToContactId === k && t.status !== 'D'},
        {name: 'expenseId', keysFn: t => t.expenses, presentFn: (t, k) => t.expenses?.includes(k)}
    ]
});
    }
}
export default Matter;
